import {CommonModule} from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {
  PixAutomaticJourney,
  PixAutomaticMandateStatus,
  Subscription,
} from '@gatewee/types/subscription';
import {PaymentMethod} from '@gatewee/types/payment';
import {interval, Subscription as RxSub, switchMap, takeWhile} from 'rxjs';
import {GateweeButtonComponent} from '../gatewee-button/gatewee-button.component';
import {TuiAlertService} from '@taiga-ui/core';

export type PixAutomaticMandateScope = 'recipient' | 'domain';

@Component({
  selector: 'gatewee-pix-automatic-mandate',
  standalone: true,
  imports: [CommonModule, TranslateModule, GateweeButtonComponent],
  templateUrl: './gatewee-pix-automatic-mandate.component.html',
})
export class GateweePixAutomaticMandateComponent
  implements OnInit, OnChanges, OnDestroy
{
  @Input({required: true}) subscription!: Subscription;
  /** API path scope for refresh + sandbox endpoints. */
  @Input() scope: PixAutomaticMandateScope = 'recipient';
  /** When true, show sandbox simulate buttons. */
  @Input() sandbox = false;
  /** Enable light polling while mandate is pending. Default true. */
  @Input() autoPoll = true;

  @Output() subscriptionChange = new EventEmitter<Subscription>();

  refreshing = false;
  sandboxBusy = false;

  private pollSub?: RxSub;

  constructor(
    private http: HttpClient,
    private translate: TranslateService,
    private alerts: TuiAlertService,
  ) {}

  ngOnInit(): void {
    this.maybeStartPoll();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['subscription'] || changes['autoPoll']) {
      this.maybeStartPoll();
    }
  }

  ngOnDestroy(): void {
    this.stopPoll();
  }

  get isPixAutomatic(): boolean {
    return (
      this.subscription?.defaultPaymentMethod === PaymentMethod.PIX_AUTOMATIC
    );
  }

  get mandateStatus(): PixAutomaticMandateStatus | undefined {
    return this.subscription?.pixAutomaticMandateStatus;
  }

  get isPending(): boolean {
    return this.mandateStatus === 'pending' || !this.mandateStatus;
  }

  get isApproved(): boolean {
    return this.mandateStatus === 'approved';
  }

  get paymentData() {
    return this.subscription?.pixAutomaticPaymentData;
  }

  get qrSrc(): string | null {
    const b64 = this.paymentData?.qrCodeBase64;
    if (!b64) {
      return null;
    }
    return b64.startsWith('data:') ? b64 : `data:image/png;base64,${b64}`;
  }

  get emv(): string | undefined {
    return this.paymentData?.emv;
  }

  get paymentLinkUrl(): string | undefined {
    return this.paymentData?.paymentLinkUrl;
  }

  get journeyLabelKey(): string | null {
    const j = this.subscription?.pixAutomaticJourney as
      | PixAutomaticJourney
      | undefined;
    if (j === 'only_recurrency') {
      return 'features.subscriptions.view.pixAutomatic.journeyOnlyRecurrency';
    }
    if (j === 'payment_on_approval') {
      return 'features.subscriptions.view.pixAutomatic.journeyPaymentOnApproval';
    }
    return null;
  }

  get statusLabelKey(): string {
    switch (this.mandateStatus) {
      case 'approved':
        return 'features.subscriptions.view.pixAutomatic.mandateApproved';
      case 'rejected':
        return 'features.subscriptions.view.pixAutomatic.mandateRejected';
      case 'canceled':
        return 'features.subscriptions.view.pixAutomatic.mandateCanceled';
      case 'pending':
      default:
        return 'features.subscriptions.view.pixAutomatic.mandatePending';
    }
  }

  get statusBadgeClass(): string {
    switch (this.mandateStatus) {
      case 'approved':
        return 'gw-bg-status-positive-pale gw-text-text-primary';
      case 'rejected':
      case 'canceled':
        return 'gw-bg-status-negative-pale gw-text-text-primary';
      case 'pending':
      default:
        return 'gw-bg-status-warning-pale gw-text-text-primary';
    }
  }

  get showSandboxTools(): boolean {
    return this.sandbox && this.isPixAutomatic;
  }

  refresh(): void {
    if (!this.subscription?.id || this.refreshing) {
      return;
    }
    this.refreshing = true;
    this.http
      .get<Subscription>(
        `/subscriptions/${this.scope}/${this.subscription.id}`,
        {
          params: {
            options: JSON.stringify({
              relations: {client: true, plan: true, recipient: true},
            }),
          },
        },
      )
      .subscribe({
        next: data => {
          this.refreshing = false;
          this.subscription = data;
          this.subscriptionChange.emit(data);
          this.maybeStartPoll();
        },
        error: () => {
          this.refreshing = false;
        },
      });
  }

  async copyEmv(): Promise<void> {
    if (!this.emv) {
      return;
    }
    try {
      await navigator.clipboard.writeText(this.emv);
      this.alerts
        .open(
          this.translate.instant(
            'features.subscriptions.view.pixAutomatic.emvCopied',
          ),
          {appearance: 'positive'},
        )
        .subscribe();
    } catch {
      /* ignore */
    }
  }

  openPaymentLink(): void {
    if (this.paymentLinkUrl) {
      window.open(this.paymentLinkUrl, '_blank', 'noopener');
    }
  }

  sandboxApprove(): void {
    this.runSandbox(
      `/subscriptions/${this.scope}/sandbox-approve-mandate/${this.subscription.id}`,
      'features.subscriptions.view.pixAutomatic.sandboxApproveSuccess',
    );
  }

  sandboxPayCobr(): void {
    this.runSandbox(
      `/subscriptions/${this.scope}/sandbox-pay-cobr/${this.subscription.id}`,
      'features.subscriptions.view.pixAutomatic.sandboxPayCobrSuccess',
    );
  }

  private runSandbox(url: string, successKey: string): void {
    if (this.sandboxBusy || !this.subscription?.id) {
      return;
    }
    this.sandboxBusy = true;
    this.http.post(url, {}).subscribe({
      next: () => {
        this.sandboxBusy = false;
        this.alerts
          .open(this.translate.instant(successKey), {appearance: 'positive'})
          .subscribe();
        this.refresh();
      },
      error: () => {
        this.sandboxBusy = false;
        this.alerts
          .open(
            this.translate.instant(
              'features.subscriptions.view.pixAutomatic.sandboxError',
            ),
            {appearance: 'negative'},
          )
          .subscribe();
      },
    });
  }

  private maybeStartPoll(): void {
    this.stopPoll();
    if (
      !this.autoPoll ||
      !this.isPixAutomatic ||
      !this.isPending ||
      !this.subscription?.id
    ) {
      return;
    }
    this.pollSub = interval(8000)
      .pipe(
        takeWhile(() => this.isPending),
        switchMap(() =>
          this.http.get<Subscription>(
            `/subscriptions/${this.scope}/${this.subscription.id}`,
            {
              params: {
                options: JSON.stringify({
                  relations: {client: true, plan: true, recipient: true},
                }),
              },
            },
          ),
        ),
      )
      .subscribe({
        next: data => {
          this.subscription = data;
          this.subscriptionChange.emit(data);
          if (data.pixAutomaticMandateStatus !== 'pending') {
            this.stopPoll();
          }
        },
      });
  }

  private stopPoll(): void {
    this.pollSub?.unsubscribe();
    this.pollSub = undefined;
  }
}
